import { useState, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BLUE, DARK, GOLD, GREEN, RED, AMBER, LIGHT } from '../constants/colors';
import { fmt, fmtK } from '../utils/formatters';
import { ALLOC_OPTIONS } from '../constants/formConfig';
import StatCard from './StatCard';
import Section from './Section';
import TabBar from './TabBar';
import ChartTip from './ChartTip';
import Slider from './Slider';

export default function FinancialModel({savings,totalValue,results}){
  const [tab,setTab]=useState("savings");
  const [alloc,setAlloc]=useState("equipment");
  const [revGrowth,setRevGrowth]=useState(10);
  const [reinvestPct,setReinvestPct]=useState(100);
  const [period,setPeriod]=useState(5);

  const baseRev=results.rev||1000000;
  const selAlloc=ALLOC_OPTIONS.find(a=>a.value===alloc);
  const reinvestAmt=savings*(reinvestPct/100);

  const chartData=useCallback(()=>{
    const totCur=Object.values(results.cur).reduce((a,b)=>a+b,0);
    const totOpt=Math.max(0,totCur-savings);
    let wor=baseRev, opt=baseRev, ind=baseRev;
    return Array.from({length:period},(_,i)=>{
      const yr=i+1;
      const reinv=savings*(reinvestPct/100);
      // Revenue model — 3 lines
      ind*=(1+Math.max(0,revGrowth-1)/100);        // -1% drag from premium inflation eating margin
      wor*=(1+revGrowth/100);
      opt*=(1+(revGrowth+4)/100);                  // +4% operational efficiency from freed cash
      opt+=reinv*selAlloc.roi;
      // Insurance cost model — 3 lines (amounts, not revenue)
      const noReviewCost=Math.round(totCur*Math.pow(1.05,yr));   // 5%/yr inflation, no review
      const marketCost=Math.round(totCur*Math.pow(1.03,yr));     // 3%/yr standard market
      const aorCost=Math.round(totOpt*Math.pow(1.03,yr));        // 3%/yr on optimized base
      return{
        year:yr,
        "Industry (No Review)":Math.round(ind),
        "Without AOR":Math.round(wor),
        "With AOR Optimization":Math.round(opt),
        "No Review Cost":noReviewCost,
        "Market Rate Cost":marketCost,
        "AOR Optimized Cost":aorCost,
        "Annual Savings":Math.round(savings*Math.pow(1.03,i)),
        "Cumulative Savings":Math.round(savings*yr*Math.pow(1.025,i)),
      };
    });
  },[savings,revGrowth,reinvestPct,period,alloc,baseRev,selAlloc,results])();

  const last=chartData[chartData.length-1];
  const totalGap=last["With AOR Optimization"]-last["Industry (No Review)"];  // AOR vs worst case
  const totalCum=last["Cumulative Savings"];
  const yr5CostGap=last["No Review Cost"]-last["AOR Optimized Cost"];
  const totalCostAdvantage=chartData.reduce((s,d)=>s+(d["No Review Cost"]-d["AOR Optimized Cost"]),0);

  // Risk exposure data — anchored to actual savings numbers
  const riskData=[
    {name:"Auto",exposure:Math.round((results.cur.auto||8000)*3.2),optimized:Math.round((results.cur.auto||8000)-(results.opt.auto||6000))},
    {name:"Gen Liability",exposure:Math.round((results.cur.gl||6000)*4.1),optimized:Math.round((results.cur.gl||6000)-(results.opt.gl||5000))},
    {name:"Workers Comp",exposure:Math.round(((results.cur.wc||8000))*2.8),optimized:Math.round((results.cur.wc||8000)-(results.opt.wc||7000))},
    {name:"Property",exposure:Math.round((results.cur.prop||results.cur.property||4000)*5.5),optimized:Math.round((results.cur.prop||results.cur.property||4000)-(results.opt.prop||results.opt.property||3500))},
  ];
  const totalExposure=riskData.reduce((a,b)=>a+b.exposure,0);
  const totalOptimized=riskData.reduce((a,b)=>a+b.optimized,0);

  // Chart gradient defs shared
  const Gradients=()=>(
    <defs>
      <linearGradient id="gBLUE" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={BLUE} stopOpacity={0.18}/><stop offset="100%" stopColor={BLUE} stopOpacity={0}/></linearGradient>
      <linearGradient id="gGOLD" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={GOLD} stopOpacity={0.22}/><stop offset="100%" stopColor={GOLD} stopOpacity={0}/></linearGradient>
      <linearGradient id="gGREEN" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={GREEN} stopOpacity={0.22}/><stop offset="100%" stopColor={GREEN} stopOpacity={0}/></linearGradient>
      <linearGradient id="gGREY" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#94a3b8" stopOpacity={0.12}/><stop offset="100%" stopColor="#94a3b8" stopOpacity={0}/></linearGradient>
      <linearGradient id="gAMBER" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={AMBER} stopOpacity={0.18}/><stop offset="100%" stopColor={AMBER} stopOpacity={0}/></linearGradient>
      <linearGradient id="gREDAREA" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={RED} stopOpacity={0.14}/><stop offset="100%" stopColor={RED} stopOpacity={0}/></linearGradient>
      <linearGradient id="gRED" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={RED} stopOpacity={0.9}/><stop offset="100%" stopColor="#b91c1c" stopOpacity={0.9}/></linearGradient>
      <linearGradient id="gGREENH" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#16a34a" stopOpacity={0.9}/><stop offset="100%" stopColor={GREEN} stopOpacity={0.9}/></linearGradient>
    </defs>
  );

  return(
    <Section title="Financial Impact Model" subtitle="Adjust sliders to model different growth scenarios in real time" icon="📊">
      <TabBar active={tab} onChange={setTab} tabs={[
        {id:"savings",icon:"💰",label:"Savings"},
        {id:"growth",icon:"📈",label:"Growth"},
        {id:"risk",icon:"🛡️",label:"Exposure"},
        {id:"forecast",icon:"🔭",label:"Forecast"},
        {id:"mod",icon:"📋",label:"WC Mod"},
      ]}/>

      {/* ── SAVINGS TAB ── */}
      {tab==="savings"&&(
        <div>
          {/* Big summary stats FIRST */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}} className="m-col1">
            <StatCard label="Year 1 AOR Value" value={fmtK(totalValue||savings)} sub="premium savings + exposure eliminated" color={GREEN} bg="#F0FDF4" border="#BBF7D0" large/>
            <StatCard label={`Yr ${period} Cost Gap`} value={fmtK(yr5CostGap)} sub="AOR vs. no-review scenario" color={RED} bg="#FEF2F2" border="#FECACA" large/>
            <StatCard label={`${period}-Yr Total Advantage`} value={fmtK(totalCostAdvantage)} sub="vs. industry (no review)" color={GOLD} bg="#FEFCE8" border="#FDE68A" large/>
          </div>
          {/* AOR avg savings comparison bar */}
          {results.avgSavingsForSize&&(
            <div style={{background:"#F8FAFC",borderRadius:12,padding:"13px 18px",marginBottom:20,border:"1px solid #E2E8F0",display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:180}}>
                <div style={{fontSize:11,fontWeight:700,color:"#475569",fontFamily:"sans-serif",marginBottom:3}}>
                  AOR Clients at {fmtK(results.rev)} Revenue · {results.sector==="realestate"?"Real Estate":"Construction"}
                </div>
                <div style={{height:8,background:"#E2E8F0",borderRadius:99,overflow:"hidden",position:"relative"}}>
                  <div style={{position:"absolute",height:"100%",width:(Math.min(savings,results.avgSavingsForSize*2)/results.avgSavingsForSize/2*100)+"%",background:`linear-gradient(90deg,${BLUE},${GREEN})`,borderRadius:99,transition:"width 1s ease"}}/>
                  <div style={{position:"absolute",height:"100%",left:"50%",width:2,background:GOLD,borderRadius:99,opacity:0.8}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",marginTop:3}}>
                  <span style={{fontSize:10,color:"#94a3b8",fontFamily:"sans-serif"}}>$0</span>
                  <span style={{fontSize:10,color:GOLD,fontFamily:"sans-serif",fontWeight:600}}>Avg: {fmt(results.avgSavingsForSize)}</span>
                  <span style={{fontSize:10,color:"#94a3b8",fontFamily:"sans-serif"}}>{fmt(results.avgSavingsForSize*2)}+</span>
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:11,color:"#94a3b8",fontFamily:"sans-serif"}}>Your estimate</div>
                <div style={{fontSize:18,fontWeight:800,color:savings>=results.avgSavingsForSize?GREEN:BLUE,fontFamily:"sans-serif",letterSpacing:"-0.01em"}}>{fmt(savings)}</div>
                <div style={{fontSize:10,fontWeight:600,color:savings>=results.avgSavingsForSize?"#16a34a":"#64748b",fontFamily:"sans-serif"}}>
                  {savings>=results.avgSavingsForSize?"↑ Above average":"Near average"}
                </div>
              </div>
            </div>
          )}

          <div style={{marginBottom:10}}>
            <p style={{margin:0,fontSize:12,color:"#94a3b8",fontFamily:"sans-serif"}}>
              Total insurance spend over time — AOR vs. market scenarios. Y-axis scaled to show divergence.
            </p>
          </div>

          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData} margin={{top:6,right:6,left:0,bottom:0}}>
              <Gradients/>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false}/>
              <XAxis dataKey="year" tick={{fontSize:12,fontFamily:"sans-serif",fill:"#94a3b8"}} axisLine={false} tickLine={false} tickFormatter={v=>`Yr ${v}`}/>
              <YAxis tick={{fontSize:11,fontFamily:"sans-serif",fill:"#94a3b8"}} tickFormatter={fmtK} width={58} axisLine={false} tickLine={false}
                domain={[dataMin=>Math.max(0,Math.floor(dataMin*0.88/100)*100),'auto']}/>
              <Tooltip content={<ChartTip/>} cursor={{stroke:`${RED}20`,strokeWidth:1}}/>
              <Legend wrapperStyle={{fontSize:12,fontFamily:"sans-serif",paddingTop:12}}/>
              <Area type="monotone" dataKey="No Review Cost" name="Industry (No Review — 5%/yr)" stroke={RED} strokeWidth={2.5} fill="url(#gREDAREA)" dot={false} activeDot={{r:5,fill:RED}}/>
              <Area type="monotone" dataKey="Market Rate Cost" name="Standard Market (3%/yr)" stroke={AMBER} strokeWidth={2} fill="url(#gAMBER)" strokeDasharray="6 3" dot={false} activeDot={{r:4,fill:AMBER}}/>
              <Area type="monotone" dataKey="AOR Optimized Cost" name="With AOR Optimization" stroke={GREEN} strokeWidth={2.5} fill="url(#gGREEN)" dot={false} activeDot={{r:5,fill:GREEN}}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── GROWTH TAB ── */}
      {tab==="growth"&&(
        <div>
          {/* Big outcome numbers at top */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:24}} className="m-col1">
            <StatCard label={`Revenue Gap — Year ${period}`} value={fmtK(totalGap)} sub="AOR vs. industry (no broker)" color={GREEN} bg="#F0FDF4" border="#BBF7D0" large/>
            <StatCard label="Reinvestment Value" value={fmtK(reinvestAmt*selAlloc.roi*period)} sub={`${fmt(reinvestAmt)}/yr at ${Math.round((selAlloc.roi-1)*100)}% ROI`} color={BLUE} bg={LIGHT} border={`${BLUE}30`} large/>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 28px",marginBottom:20}} className="m-col1">
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:"sans-serif",marginBottom:10}}>Reinvestment Strategy</div>
              {ALLOC_OPTIONS.map(a=>(
                <div key={a.value} onClick={()=>setAlloc(a.value)} style={{padding:"10px 13px",border:`1.5px solid ${alloc===a.value?BLUE:"#E2E8F0"}`,borderRadius:10,marginBottom:7,cursor:"pointer",background:alloc===a.value?LIGHT:"#fff",transition:"all 0.18s",display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${alloc===a.value?BLUE:"#CBD5E1"}`,background:alloc===a.value?BLUE:"#fff",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {alloc===a.value&&<div style={{width:7,height:7,borderRadius:"50%",background:"#fff"}}/>}
                  </div>
                  <div>
                    <div style={{fontSize:13,fontWeight:alloc===a.value?700:500,color:alloc===a.value?BLUE:DARK,fontFamily:"sans-serif"}}>{a.label}</div>
                    <div style={{fontSize:11,color:"#94a3b8",fontFamily:"sans-serif"}}>{a.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:"sans-serif",marginBottom:10}}>Growth Assumptions</div>
              <Slider label="Revenue Growth Rate" value={revGrowth} onChange={setRevGrowth} min={0} max={40} step={1} format={v=>`${v}%`} tooltip="Expected annual revenue growth"/>
              <Slider label="Savings to Reinvest" value={reinvestPct} onChange={setReinvestPct} min={10} max={100} step={5} format={v=>`${v}%`} tooltip="Portion of insurance savings reinvested"/>
              <Slider label="Forecast Period" value={period} onChange={setPeriod} min={1} max={10} step={1} format={v=>`${v} yrs`}/>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData} margin={{top:6,right:6,left:0,bottom:0}}>
              <Gradients/>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false}/>
              <XAxis dataKey="year" tick={{fontSize:12,fontFamily:"sans-serif",fill:"#94a3b8"}} axisLine={false} tickLine={false} tickFormatter={v=>`Yr ${v}`}/>
              <YAxis tick={{fontSize:11,fontFamily:"sans-serif",fill:"#94a3b8"}} tickFormatter={fmtK} width={58} axisLine={false} tickLine={false}
                domain={[dataMin=>Math.max(0,Math.floor(dataMin*0.92/10000)*10000),'auto']}/>
              <Tooltip content={<ChartTip/>} cursor={{stroke:`${GREEN}20`,strokeWidth:1}}/>
              <Legend wrapperStyle={{fontSize:12,fontFamily:"sans-serif",paddingTop:12}}/>
              <Area type="monotone" dataKey="Industry (No Review)" name="Industry Avg (No Broker)" stroke="#64748b" strokeWidth={2} fill="url(#gGREY)" strokeDasharray="5 4" dot={false}/>
              <Area type="monotone" dataKey="Without AOR" name="Without AOR" stroke={AMBER} strokeWidth={2} fill="url(#gAMBER)" strokeDasharray="6 3" dot={false}/>
              <Area type="monotone" dataKey="With AOR Optimization" stroke={GREEN} strokeWidth={2.5} fill="url(#gGREEN)" dot={false} activeDot={{r:5,fill:GREEN}}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── RISK / EXPOSURE TAB — completely redesigned ── */}
      {tab==="risk"&&(
        <div>
          {/* HERO exposure numbers — cannot be missed */}
          <div style={{background:"linear-gradient(135deg,#1a1a2e 0%,#16213e 60%,#0f3460 100%)",borderRadius:16,padding:"28px 28px 24px",marginBottom:20,position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",top:-40,right:-40,width:160,height:160,borderRadius:"50%",background:`${RED}12`}}/>
            <div style={{position:"absolute",bottom:-30,left:-20,width:120,height:120,borderRadius:"50%",background:`${GREEN}10`}}/>
            <div style={{position:"relative"}}>
              <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",fontFamily:"sans-serif",textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:18}}>Financial Risk Summary</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
                <div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",fontFamily:"sans-serif",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>Max Uncovered Exposure</div>
                  <div style={{fontSize:42,fontWeight:900,color:"#FC8F8F",fontFamily:"sans-serif",letterSpacing:"-0.03em",lineHeight:1}}>{fmtK(totalExposure)}</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.3)",fontFamily:"sans-serif",marginTop:4}}>potential uncovered loss across all lines</div>
                </div>
                <div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.45)",fontFamily:"sans-serif",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>Annual Premium Savings</div>
                  <div style={{fontSize:42,fontWeight:900,color:"#86EFAC",fontFamily:"sans-serif",letterSpacing:"-0.03em",lineHeight:1}}>{fmt(savings)}</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.3)",fontFamily:"sans-serif",marginTop:4}}>recaptured with AOR optimization</div>
                </div>
              </div>
              {/* Exposure reduction bar */}
              <div>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                  <span style={{fontSize:11,color:"rgba(255,255,255,0.5)",fontFamily:"sans-serif",textTransform:"uppercase",letterSpacing:"0.08em"}}>Exposure reduction</span>
                  <span style={{fontSize:15,fontWeight:900,color:"#86EFAC",fontFamily:"sans-serif"}}>{Math.round((totalOptimized/totalExposure)*100)}% recaptured</span>
                </div>
                <div style={{height:10,background:"rgba(255,255,255,0.08)",borderRadius:99,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${Math.round((totalOptimized/totalExposure)*100)}%`,background:"linear-gradient(90deg,#16a34a,#4ade80)",borderRadius:99,boxShadow:"0 0 10px rgba(74,222,128,0.4)"}}/>
                </div>
              </div>
            </div>
          </div>

          {/* Per-line breakdown — horizontal bars, big numbers */}
          <div style={{marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:"sans-serif",marginBottom:12}}>Exposure & Savings by Coverage Line</div>
            {riskData.map((row,i)=>{
              const exposurePct=Math.round((row.exposure/totalExposure)*100);
              const savingsPct=Math.round((row.optimized/totalOptimized)*100);
              return(
                <div key={i} style={{marginBottom:12,padding:"18px 20px",background:"#fff",borderRadius:14,border:"1px solid #E2E8F0",boxShadow:"0 2px 8px rgba(0,0,0,0.04)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:DARK,fontFamily:"sans-serif"}}>{row.name}</div>
                      <div style={{fontSize:11,color:"#94a3b8",fontFamily:"sans-serif",marginTop:2}}>{exposurePct}% of total exposure profile</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:28,fontWeight:900,color:GREEN,fontFamily:"sans-serif",letterSpacing:"-0.03em",lineHeight:1}}>{fmt(row.optimized)}</div>
                      <div style={{fontSize:10,color:"#94a3b8",fontFamily:"sans-serif",marginTop:3,textTransform:"uppercase",letterSpacing:"0.06em"}}>saved / year</div>
                    </div>
                  </div>
                  {/* Two stacked bars: exposure and savings */}
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    <div>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                        <span style={{fontSize:10,color:RED,fontFamily:"sans-serif",fontWeight:600}}>Uncovered exposure</span>
                        <span style={{fontSize:11,fontWeight:700,color:RED,fontFamily:"sans-serif"}}>{fmtK(row.exposure)}</span>
                      </div>
                      <div style={{height:7,background:"#FEE2E2",borderRadius:99,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${exposurePct}%`,background:"url(#gRED)",backgroundColor:RED,borderRadius:99,opacity:0.7}}/>
                      </div>
                    </div>
                    <div>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                        <span style={{fontSize:10,color:GREEN,fontFamily:"sans-serif",fontWeight:600}}>Premium savings recovered</span>
                        <span style={{fontSize:11,fontWeight:700,color:GREEN,fontFamily:"sans-serif"}}>{fmt(row.optimized)}/yr</span>
                      </div>
                      <div style={{height:7,background:"#DCFCE7",borderRadius:99,overflow:"hidden"}}>
                        <div style={{height:"100%",width:`${savingsPct}%`,backgroundColor:GREEN,borderRadius:99,opacity:0.8}}/>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom call-out */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}} className="m-col1">
            <div style={{background:"#FEF2F2",borderRadius:12,padding:"16px 18px",border:"1px solid #FECACA"}}>
              <div style={{fontSize:11,color:RED,fontFamily:"sans-serif",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Current Risk Exposure</div>
              <div style={{fontSize:36,fontWeight:900,color:RED,fontFamily:"sans-serif",letterSpacing:"-0.03em",lineHeight:1}}>{fmtK(totalExposure)}</div>
              <div style={{fontSize:12,color:"#64748b",fontFamily:"sans-serif",marginTop:4}}>potential uncovered across all lines</div>
            </div>
            <div style={{background:"#F0FDF4",borderRadius:12,padding:"16px 18px",border:"1px solid #BBF7D0"}}>
              <div style={{fontSize:11,color:"#16a34a",fontFamily:"sans-serif",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>With AOR Optimization</div>
              <div style={{fontSize:36,fontWeight:900,color:"#16a34a",fontFamily:"sans-serif",letterSpacing:"-0.03em",lineHeight:1}}>{fmt(savings)}</div>
              <div style={{fontSize:12,color:"#64748b",fontFamily:"sans-serif",marginTop:4}}>saved annually — exposure minimized</div>
            </div>
          </div>
        </div>
      )}

      {/* ── FORECAST TAB ── */}
      {tab==="forecast"&&(
        <div>
          {/* Big forecast numbers first */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:22}} className="m-col2">
            <StatCard label="Year 1 AOR Value" value={fmtK(totalValue||savings)} color={GREEN} bg="#F0FDF4" border="#BBF7D0"/>
            <StatCard label={`Yr ${period} Cost Gap`} value={fmtK(yr5CostGap)} sub="AOR vs. no-review" color={RED} bg="#FEF2F2" border="#FECACA"/>
            <StatCard label="Revenue Gap" value={fmtK(totalGap)} sub="vs. industry (no broker)" color={BLUE} bg={LIGHT} border={`${BLUE}30`}/>
            <StatCard label={`${period}-Yr Advantage`} value={fmtK(totalCostAdvantage)} sub="total cost savings" color={GOLD} bg="#FEFCE8" border="#FDE68A"/>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 24px",marginBottom:6}} className="m-col1">
            <Slider label="Revenue Growth / Year" value={revGrowth} onChange={setRevGrowth} min={0} max={40} step={1} format={v=>`${v}%`} tooltip="Expected annual revenue growth"/>
            <Slider label="Savings to Reinvest" value={reinvestPct} onChange={setReinvestPct} min={10} max={100} step={5} format={v=>`${v}%`}/>
            <Slider label="Forecast Period" value={period} onChange={setPeriod} min={3} max={10} step={1} format={v=>`${v} years`}/>
            <div/>
          </div>

          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData} margin={{top:6,right:6,left:0,bottom:0}}>
              <Gradients/>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false}/>
              <XAxis dataKey="year" tick={{fontSize:12,fontFamily:"sans-serif",fill:"#94a3b8"}} axisLine={false} tickLine={false} tickFormatter={v=>`Year ${v}`}/>
              <YAxis tick={{fontSize:11,fontFamily:"sans-serif",fill:"#94a3b8"}} tickFormatter={fmtK} width={58} axisLine={false} tickLine={false}
                domain={[dataMin=>Math.max(0,Math.floor(dataMin*0.90/10000)*10000),'auto']}/>
              <Tooltip content={<ChartTip/>} cursor={{stroke:`${BLUE}20`,strokeWidth:1}}/>
              <Legend wrapperStyle={{fontSize:12,fontFamily:"sans-serif",paddingTop:12}}/>
              <Area type="monotone" dataKey="Industry (No Review)" name="Industry Avg (No Broker)" stroke="#64748b" strokeWidth={2} fill="url(#gGREY)" strokeDasharray="5 4" dot={false}/>
              <Area type="monotone" dataKey="Without AOR" name="Without AOR" stroke={AMBER} strokeWidth={2} fill="url(#gAMBER)" strokeDasharray="6 3" dot={false}/>
              <Area type="monotone" dataKey="With AOR Optimization" stroke={BLUE} strokeWidth={2.5} fill="url(#gBLUE)" dot={false} activeDot={{r:5,fill:BLUE}}/>
            </AreaChart>
          </ResponsiveContainer>

          <div style={{background:"linear-gradient(135deg,#F0FDF4,#EAF4FB)",borderRadius:12,padding:"14px 18px",border:"1px solid #BBF7D0",marginTop:16}}>
            <p style={{margin:0,fontSize:13,color:DARK,fontFamily:"sans-serif",lineHeight:1.7}}>
              <strong>Forecast Summary:</strong> Over {period} years, AOR optimization creates a <strong style={{color:RED,fontSize:15}}>{fmtK(totalCostAdvantage)}</strong> insurance cost advantage vs. the no-review scenario and a <strong style={{color:BLUE,fontSize:15}}>{fmtK(totalGap)}</strong> revenue gap vs. the industry average — driven by savings reinvestment at {Math.round((selAlloc.roi-1)*100)}% ROI and a 4% annual efficiency gain.
            </p>
          </div>
        </div>
      )}

      {/* ── WC MOD TAB ── */}
      {tab==="mod"&&(()=>{
        if(!results.expModEntered){
          return(
            <div style={{textAlign:"center",padding:"48px 24px"}}>
              <div style={{fontSize:40,marginBottom:16}}>📋</div>
              <div style={{fontSize:17,fontWeight:700,color:DARK,fontFamily:"sans-serif",marginBottom:8}}>Enter Your Experience Mod to See Your Full Analysis</div>
              <div style={{fontSize:13,color:"#64748b",fontFamily:"sans-serif",maxWidth:420,margin:"0 auto"}}>
                Your WC experience mod (EMR) is on your current workers' comp policy declarations page. Enter it in Step 1 above to see how it's affecting your premiums and what AOR can do to improve it.
              </div>
            </div>
          );
        }
        const expMod=results.expMod||1.0;
        const wcCur=results.wcEst||results.cur.wc||0;
        const baseWC=Math.round(wcCur/expMod/100)*100;
        const wc95=Math.round(baseWC*0.95/100)*100;
        const modDiff=wcCur-baseWC;
        const isAbove=expMod>1.0;
        const isBelow=expMod<1.0;

        // Context badge
        let badgeLabel, badgeColor, badgeBg;
        if(expMod<0.85){badgeLabel="Excellent — Top 10%";badgeColor="#16a34a";badgeBg="#F0FDF4";}
        else if(expMod<1.0){badgeLabel="Good — Below Average Risk";badgeColor=GREEN;badgeBg="#F0FDF4";}
        else if(expMod===1.0){badgeLabel="Average";badgeColor="#64748b";badgeBg="#F8FAFC";}
        else if(expMod<1.25){badgeLabel="Above Average — Improvable";badgeColor=AMBER;badgeBg="#FFFBEB";}
        else{badgeLabel="High Risk — Immediate Action Needed";badgeColor=RED;badgeBg="#FEF2F2";}

        return(
          <div>
            {/* 1. MOD CONTEXT BADGE */}
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:22,padding:"14px 18px",borderRadius:12,background:badgeBg,border:`1px solid ${badgeColor}30`}}>
              <div style={{fontSize:32,lineHeight:1}}>📋</div>
              <div>
                <div style={{fontSize:11,color:"#94a3b8",fontFamily:"sans-serif",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Your Experience Mod Rating</div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:28,fontWeight:900,color:badgeColor,fontFamily:"sans-serif",letterSpacing:"-0.02em"}}>{expMod.toFixed(2)}</span>
                  <span style={{fontSize:12,fontWeight:700,color:badgeColor,background:`${badgeColor}15`,padding:"3px 10px",borderRadius:99,fontFamily:"sans-serif",border:`1px solid ${badgeColor}30`}}>{badgeLabel}</span>
                </div>
              </div>
            </div>

            {/* 2. MOD IMPACT TABLE */}
            <div style={{marginBottom:22}}>
              <div style={{fontSize:11,fontWeight:700,color:"#475569",textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:"sans-serif",marginBottom:10}}>WC Premium Impact Breakdown</div>
              <div style={{borderRadius:12,border:"1px solid #E2E8F0",overflow:"hidden"}}>
                {[
                  {label:"Your Current WC Premium",mod:`${expMod.toFixed(2)} (actual)`,amount:wcCur,highlight:false},
                  {label:"WC at Mod 1.00 (Baseline)",mod:"1.00",amount:baseWC,highlight:false},
                  {label:"WC at Mod 0.95 (Achievable)",mod:"0.95",amount:wc95,highlight:true},
                ].map((row,i)=>(
                  <div key={i} style={{display:"grid",gridTemplateColumns:"1fr auto auto",gap:16,alignItems:"center",padding:"14px 18px",background:row.highlight?"#F0FDF4":"#fff",borderBottom:i<2?"1px solid #F1F5F9":"none"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:row.highlight?700:500,color:row.highlight?"#16a34a":DARK,fontFamily:"sans-serif"}}>{row.label}</div>
                    </div>
                    <div style={{fontSize:11,color:"#94a3b8",fontFamily:"sans-serif",textAlign:"right",whiteSpace:"nowrap"}}>Mod: {row.mod}</div>
                    <div style={{fontSize:16,fontWeight:700,color:row.highlight?"#16a34a":DARK,fontFamily:"sans-serif",textAlign:"right",minWidth:90}}>{fmt(row.amount)}</div>
                  </div>
                ))}
              </div>
              {/* Overpaying / credit callout */}
              <div style={{marginTop:10,padding:"12px 16px",borderRadius:10,background:isAbove?"#FEF2F2":isBelow?"#F0FDF4":"#F8FAFC",border:`1px solid ${isAbove?"#FECACA":isBelow?"#BBF7D0":"#E2E8F0"}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:13,fontWeight:600,color:isAbove?RED:isBelow?"#16a34a":"#64748b",fontFamily:"sans-serif"}}>
                  {isAbove?"Overpaying due to mod":isBelow?"Mod credit saving you":"At market baseline"}
                </span>
                <span style={{fontSize:18,fontWeight:900,color:isAbove?RED:isBelow?"#16a34a":"#64748b",fontFamily:"sans-serif"}}>
                  {isAbove?"+":isBelow?"-":""}{fmt(Math.abs(modDiff))}/yr
                </span>
              </div>
            </div>

            {/* 3. IF MOD > 1.0 CALLOUT */}
            {isAbove&&(
              <div style={{marginBottom:22,padding:"18px 20px",borderRadius:12,background:"linear-gradient(135deg,#FEF2F2,#FFF1F1)",border:`2px solid ${RED}30`}}>
                <div style={{fontSize:12,fontWeight:700,color:RED,fontFamily:"sans-serif",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Cost of Your Elevated Mod</div>
                <p style={{margin:"0 0 8px",fontSize:14,color:DARK,fontFamily:"sans-serif",lineHeight:1.7}}>
                  A mod of <strong>{expMod.toFixed(2)}</strong> is costing you an extra <strong style={{color:RED}}>{fmt(modDiff)}</strong> per year above market average. Over 3 years that is <strong style={{color:RED,fontSize:15}}>{fmt(modDiff*3)}</strong> in excess WC premiums.
                </p>
                <p style={{margin:0,fontSize:12,color:"#64748b",fontFamily:"sans-serif",lineHeight:1.6}}>
                  Every 0.05 reduction in your mod saves approximately <strong>{fmt(Math.round(baseWC*0.05/100)*100)}/yr</strong>. AOR's mod improvement programs target measurable reductions within 2–3 policy years.
                </p>
              </div>
            )}

            {/* 4. HONEST TIMELINE STATEMENT */}
            <div style={{marginBottom:22,padding:"14px 18px",borderRadius:10,background:"#F8FAFC",border:"1px solid #E2E8F0"}}>
              <div style={{fontSize:11,fontWeight:700,color:"#475569",fontFamily:"sans-serif",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>How Experience Mods Work</div>
              <p style={{margin:0,fontSize:13,color:"#475569",fontFamily:"sans-serif",lineHeight:1.7}}>
                Experience mods are calculated on a 3-year rolling window excluding the most recent policy year. This means improvements made today will begin to show in your mod in 12–24 months and reach full effect in 36–48 months. There is no shortcut — but consistent loss control and claims management produce reliable, auditable results.
              </p>
            </div>

            {/* 5. AOR MOD SERVICES CALLOUT */}
            <div style={{borderRadius:12,border:`1.5px solid ${BLUE}30`,overflow:"hidden"}}>
              <div style={{padding:"12px 18px",background:BLUE,display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:16}}>🛡️</span>
                <span style={{fontSize:13,fontWeight:700,color:"#fff",fontFamily:"sans-serif"}}>AOR Mod Improvement Programs</span>
              </div>
              {[
                {icon:"📉",title:"Loss Control Programs",desc:"On-site safety consulting, OSHA compliance reviews, and claim frequency reduction strategies targeting your highest-cost exposure classes."},
                {icon:"⚖️",title:"Claims Advocacy",desc:"Active management of open claims, return-to-work programs, and reserve challenges to minimize the losses that drive your mod up."},
                {icon:"🔍",title:"Payroll Audit Defense",desc:"Unit stat filing review and payroll audit verification — catching misclassifications and overcharges that inflate your mod calculation."},
              ].map((svc,i)=>(
                <div key={i} style={{padding:"14px 18px",borderTop:"1px solid #E2E8F0",display:"flex",gap:14,alignItems:"flex-start",background:"#fff"}}>
                  <div style={{fontSize:20,lineHeight:1,flexShrink:0,marginTop:1}}>{svc.icon}</div>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:DARK,fontFamily:"sans-serif",marginBottom:3}}>{svc.title}</div>
                    <div style={{fontSize:12,color:"#64748b",fontFamily:"sans-serif",lineHeight:1.6}}>{svc.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </Section>
  );
}

